import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Input, Button, Grid, Header, Modal } from 'semantic-ui-react';
import _ from 'lodash';

// Components
import { AsyncSelect } from 'components/Forms/UI/AsyncSelect';
import { BtnWithActions } from 'components/Buttons';
import AnagraficaForm from 'components/Forms/Anagrafica';
import { mapToInitialValues, initialValues } from 'components/Forms/Anagrafica/Base/initialValues';
import { mapToClienteInitialValues, clienteInitialValues } from 'components/Forms/Anagrafica/Cliente/initialValues';
import FullModal from 'components/Modal';

// Constants
import { ACL } from 'shared/constants';

// Services
import AclService from '../../../shared/lib/acl';
import AnagraficaService from '../../../shared/services/api/anagrafica';
import ProvenienzaService from '../../../shared/services/api/provenienza';
import GestioneCollaboratoreService from '../../../shared/services/rpc/gestione-collaboratore';

class ImpresaIntestatario extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
      isLoading: false,
      searchedAnagrafica: null,

      isSelectCollaboratoreOpen: false,
      selectedCollaboratore: this.props.auth.collaboratoreId,

      modalAnagraficaOpened: false,
      selectedAnagrafica: {},
      provenienzeOptions: null,
      isLoadingAnagrafica: false,
    };
  }

  componentDidMount() {
    // Load provenienza options for the anagrafica form
    ProvenienzaService.getOptions()
      .then((provenienzeOptions) => {
        this.setState({ provenienzeOptions });
      })
      .catch(() => {
        // Silently fail - provenienza options are optional
      });
  }

  onCreateImpresa = () => {
    if (!this.props.auth.agenzie.current) {
      this.props.notification().addNotification({
        message: "Prima di procedere con la creazione selezionare un'agenzia nella topBar",
        level: 'warning',
      });

      return;
    }

    if (AclService.check(this.props.auth, ACL.CAN_ASSIGN_COLLABORATORE)) {
      this.setState({ isSelectCollaboratoreOpen: true });
    } else {
      this.props.createImpresa(
        this.state.searchedAnagrafica.id,
        this.props.auth.agenzie.current,
        this.props.auth.collaboratoreId
      );
    }
  };

  onSelectCollaboratore = () => {
    if (!this.props.auth.agenzie.current) {
      this.props.notification().addNotification({
        message: "Prima di procedere con la creazione selezionare un'agenzia nella topBar",
        level: 'warning',
      });

      return;
    }

    if (_.isEmpty(this.state.selectedCollaboratore)) {
      this.props.notification().addNotification({
        message: 'Selezionare un collaboratore',
        level: 'warning',
      });

      return;
    }

    this.onCloseSelectCollaboratore();
    this.props.createImpresa(
      this.state.searchedAnagrafica.id,
      this.props.auth.agenzie.current,
      this.state.selectedCollaboratore
    );
  };

  onCloseSelectCollaboratore = () => this.setState({ isSelectCollaboratoreOpen: false });

  handleSearchClick = () => {
    if (_.isEmpty(this.state.searchText) || this.state.searchText.length <= 5) {
      this.props.notification().addNotification({
        message: 'Inserire una partita IVA valida',
        level: 'warning',
      });

      return;
    }

    this.setState({ isLoading: true });
    AnagraficaService.getImpreseByPIVA(this.state.searchText)
      .then((res) => {
        const anagrafica = _.get(res, '_embedded.anagrafica[0]');
        if (_.isEmpty(anagrafica)) {
          this.props.notification().addNotification({
            message: 'Anagrafica non trovata',
            level: 'warning',
          });
        } else {
          this.setState({ searchedAnagrafica: anagrafica });
        }
      })
      .catch(() => {
        this.props.notification().addNotification({
          message: "Impossibile recuperare l'anagrafica",
          level: 'error',
        });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  /** Load collaboratori */
  loadCollaboratori = () =>
    GestioneCollaboratoreService.getCollaboratoreByTipoOption(
      null,
      null,
      _.get(this.props.auth.agenzie, 'current') ? [_.get(this.props.auth.agenzie, 'current')] : [],
      null,
      null,
      true
    );

  onOpenModalAnagrafica = () => {
    const { impresa } = this.props;
    
    if (!impresa || !impresa.intestatario || !impresa.intestatario.id) {
      return;
    }

    this.setState({ isLoadingAnagrafica: true, modalAnagraficaOpened: true });

    // Fetch the full anagrafica data
    AnagraficaService.get(impresa.intestatario.id)
      .then((anagrafica) => {
        this.setState({ 
          selectedAnagrafica: anagrafica,
          isLoadingAnagrafica: false 
        });
      })
      .catch(() => {
        this.props.notification().addNotification({
          message: "Impossibile caricare i dati dell'anagrafica",
          level: 'error',
        });
        this.setState({ 
          modalAnagraficaOpened: false,
          isLoadingAnagrafica: false 
        });
      });
  };

  onCloseModalAnagrafica = () => {
    this.setState({ 
      modalAnagraficaOpened: false, 
      selectedAnagrafica: {} 
    });
    
    // Refresh impresa data if needed
    if (this.props.onRefresh) {
      this.props.onRefresh();
    }
  };

  getActions = () =>
    [
      this.props.impresa &&
        this.props.impresa.intestatario &&
        this.props.impresa.intestatario.id && {
          icon: 'edit',
          label: 'Modifica anagrafica',
          action: () => this.onOpenModalAnagrafica(),
        },
    ].filter(Boolean);

  handleInputChange(text) {
    this.setState({ searchText: text });
  }

  render() {
    const { isNew, isCreatingImpresa, impresa, notification } = this.props;
    const { 
      searchedAnagrafica, 
      isSelectCollaboratoreOpen, 
      modalAnagraficaOpened, 
      selectedAnagrafica,
      provenienzeOptions,
      isLoadingAnagrafica 
    } = this.state;

    if (isNew) {
      return (
        <div style={{ padding: '0px 10px', marginBottom: '10px' }}>
          <Header as="h2">
            <Icon name="id card" />
            <Header.Content>Intestatario processo impresa</Header.Content>
            {isNew ? (
              <Header.Subheader>
                Inserire la partita IVA esatta del soggetto che si vuole aggiungere come intestatario
              </Header.Subheader>
            ) : null}
          </Header>
          <Grid>
            <Grid.Column width={10}>
              <Input
                fluid
                mandatory
                disabled={this.state.isLoading}
                loading={this.state.isLoading}
                placeholder="Inserisci la partita IVA dell'intestatario"
                value={this.state.searchText}
                onChange={(_e, data) => this.handleInputChange(data.value)}
              />
            </Grid.Column>
            <Grid.Column width={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button
                labelPosition="right"
                disabled={this.state.isLoading}
                primary
                icon="search"
                onClick={this.handleSearchClick}
                content="Cerca anagrafica"
              />
            </Grid.Column>
          </Grid>
          {!_.isEmpty(searchedAnagrafica) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div
                style={{
                  marginTop: '8px',
                  border: '1px solid #d4d4d5',
                  borderRadius: '5px',
                  padding: '10px',
                  width: '100%',
                }}
              >
                <Header style={{ textTransform: 'uppercase' }}>
                  {searchedAnagrafica.nominativo}
                  <Header.Subheader>P.IVA: {searchedAnagrafica.partitaiva}</Header.Subheader>
                </Header>
              </div>
              <Button
                onClick={() => this.onCreateImpresa()}
                type="button"
                icon="save"
                labelPosition="right"
                positive
                content="Crea pratica"
                loading={isCreatingImpresa}
                disabled={isCreatingImpresa}
                style={{ width: '250px', marginTop: '8px' }}
              />
            </div>
          ) : null}
          <Modal open={isSelectCollaboratoreOpen} onClose={this.onCloseSelectCollaboratore} size="mini">
            <Modal.Header>Seleziona il collaboratore</Modal.Header>
            <Modal.Content>
              <AsyncSelect
                loadOptions={this.loadCollaboratori}
                value={this.state.selectedCollaboratore}
                onChange={(_event, { value }) => this.setState({ selectedCollaboratore: value })}
                onBlur={() => {}}
                error={''}
                touched={{}}
                name="collaboratore"
                label="Collaboratore"
              />
            </Modal.Content>
            <Modal.Actions>
              <Button content="Annulla" onClick={() => this.onCloseSelectCollaboratore()} />
              <Button onClick={() => this.onSelectCollaboratore()} positive content="Crea pratica" />
            </Modal.Actions>
          </Modal>
        </div>
      );
    }

    return (
      <div style={{ padding: '0px 10px', marginBottom: '10px' }}>
        <Header as="h2">
          <Icon name="id card" />
          <Header.Content>Intestatario processo impresa</Header.Content>
        </Header>
        {impresa && impresa.intestatario ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                marginTop: '8px',
                border: '1px solid #d4d4d5',
                borderRadius: '5px',
                padding: '10px',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Header
                style={{
                  marginBottom: 0,
                  textTransform: 'uppercase',
                }}
              >
                {impresa.intestatario.nominativo}
                <Header.Subheader>P.IVA: {impresa.intestatario.partitaiva}</Header.Subheader>
              </Header>
              <BtnWithActions actions={this.getActions()} otherActions={[]} />
            </div>
          </div>
        ) : null}
        <FullModal
          hiddenHeader
          open={modalAnagraficaOpened}
          onClose={this.onCloseModalAnagrafica}
          title={
            <h2>
              <Icon name="id card" /> Modifica anagrafica
            </h2>
          }
          size="tiny"
        >
          {isLoadingAnagrafica ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Caricamento...
            </div>
          ) : (
            selectedAnagrafica && selectedAnagrafica.id && (
              <AnagraficaForm
                ruolo="Anagrafica"
                validationType="anagrafica"
                onEdit={() => {}}
                update={() => {}}
                notifications={{ addNotification: notification().addNotification }}
                reconcile={() => {}}
                close={this.onCloseModalAnagrafica}
                initialValues={mapToInitialValues(selectedAnagrafica, provenienzeOptions)}
                cliente={mapToClienteInitialValues(selectedAnagrafica)}
                auth={this.props.auth}
              />
            )
          )}
        </FullModal>
      </div>
    );
  }
}

ImpresaIntestatario.propTypes = {
  /** Function called on save the intestatario and creating processo impresa */
  createImpresa: PropTypes.func,
  /** Impresa data */
  impresa: PropTypes.object,
  /** Boolean if is a new impresa or existing one */
  isNew: PropTypes.bool,
  /** Function for call the notification system */
  notification: PropTypes.func,
  /** Manage loader of create new Impresa */
  isCreatingImpresa: PropTypes.bool,
  /** Object with data of logged user */
  auth: PropTypes.object,
  /** Function to refresh impresa data after anagrafica update */
  onRefresh: PropTypes.func,
};

export default ImpresaIntestatario;



// WEBPACK FOOTER //
// ./app/components/ProcessoImpresa/ImpresaIntestatario/index.js